import React from "react";
import { Table } from "../../../components/Table";
import PayoutTableCell from "./PayoutTableCell";
import { cx, payoutUi } from "./payoutDesignSystem";

type PayoutDataTableProps = {
  title?: string;
  headerAction?: React.ReactNode;
  headerContent?: React.ReactNode;
  columns: Array<string | { key: string; label: React.ReactNode }>;
  rows: React.ReactNode[][];
  emptyState?: React.ReactNode;
  footer?: React.ReactNode;
  minWidth?: string;
  className?: string;
};

function PayoutDataTable({
  title,
  headerAction,
  headerContent,
  columns,
  rows,
  emptyState,
  footer,
  minWidth = "min-w-[1080px]",
  className = "",
}: PayoutDataTableProps) {
  const surfaceRef = React.useRef<HTMLDivElement>(null);
  const tableScrollRef = React.useRef<HTMLElement | null>(null);
  const bottomScrollRef = React.useRef<HTMLDivElement>(null);
  const [scrollWidth, setScrollWidth] = React.useState(0);
  const normalizedRows = React.useMemo(
    () =>
      rows.map((row) =>
        row.map((cell) =>
          typeof cell === "string" || typeof cell === "number" ? (
            <PayoutTableCell value={cell} />
          ) : (
            cell
          ),
        ),
      ),
    [rows],
  );

  React.useEffect(() => {
    const tableScroll =
      surfaceRef.current?.querySelector<HTMLElement>(".payout-table-scroll") ||
      null;
    const bottomScroll = bottomScrollRef.current;

    if (!tableScroll || !bottomScroll) return;

    tableScrollRef.current = tableScroll;

    const updateScrollWidth = () => {
      setScrollWidth(tableScroll.scrollWidth);
      bottomScroll.scrollLeft = tableScroll.scrollLeft;
    };

    const syncFromTable = () => {
      if (bottomScroll.scrollLeft !== tableScroll.scrollLeft) {
        bottomScroll.scrollLeft = tableScroll.scrollLeft;
      }
    };

    const syncFromBottom = () => {
      if (tableScroll.scrollLeft !== bottomScroll.scrollLeft) {
        tableScroll.scrollLeft = bottomScroll.scrollLeft;
      }
    };

    updateScrollWidth();
    tableScroll.addEventListener("scroll", syncFromTable);
    bottomScroll.addEventListener("scroll", syncFromBottom);

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateScrollWidth)
        : null;
    resizeObserver?.observe(tableScroll);
    if (tableScroll.firstElementChild) {
      resizeObserver?.observe(tableScroll.firstElementChild);
    }

    return () => {
      tableScroll.removeEventListener("scroll", syncFromTable);
      bottomScroll.removeEventListener("scroll", syncFromBottom);
      resizeObserver?.disconnect();
      if (tableScrollRef.current === tableScroll) {
        tableScrollRef.current = null;
      }
    };
  }, [columns, normalizedRows, minWidth]);

  return (
    <div
      ref={surfaceRef}
      className={cx(payoutUi.table.surface, className)}
    >
      {(title || headerAction) && (
        <div className={payoutUi.table.header}>
          {title ? (
            <h2 className={payoutUi.table.title}>{title}</h2>
          ) : (
            <span />
          )}
          {headerAction}
        </div>
      )}
      {headerContent && <div className="mb-4 bg-white">{headerContent}</div>}
      <div className={payoutUi.table.base}>
        {rows.length ? (
          <Table
            data={[columns, ...normalizedRows]}
            bgColor=" bg-[#F6F8FA]"
            boxPadding=" p-0"
            minHeight=" min-h-0"
            copyRight={false}
            headerRowClassName={payoutUi.table.headerRow}
            headerCellClassName={payoutUi.table.headerCell}
            rowClassName={payoutUi.table.row}
            cellWrapperClassName={payoutUi.table.cell}
            tableClassName={minWidth}
            tableWrapperClassName={payoutUi.table.scroll}
          />
        ) : (
          <div className="payout-table-scroll">{emptyState}</div>
        )}
      </div>
      {rows.length > 0 && (
        <div
          ref={bottomScrollRef}
          className="payout-table-bottom-scroll"
          aria-label="Scroll table horizontally"
          tabIndex={0}
        >
          <div style={{ width: scrollWidth || "100%", height: 1 }} />
        </div>
      )}
      {footer}
    </div>
  );
}

export default PayoutDataTable;
